import React from 'react';
import { Helmet } from 'react-helmet-async';

// Las Vegas local SEO keywords
const LAS_VEGAS_KEYWORDS = 'Las Vegas SaaS development, Las Vegas web development, Las Vegas IT services, Nevada SaaS developer, Las Vegas custom software, Nevada tech, Las Vegas software company, Nevada web developer, Las Vegas app development, Henderson IT services, North Las Vegas software, Summerlin IT services';

const SEO = ({ 
  title, 
  description, 
  keywords,
  image,
  url,
  type = 'website',
  structuredData,
  isLocalPage = false,
  serviceName = null
}) => {
  const siteUrl = 'https://fearlessit.io';
  const defaultImage = 'https://customer-assets.emergentagent.com/job_1c71d773-84d6-4251-9bae-60816a9265db/artifacts/98drju3w_image.png';
  
  const fullTitle = title 
    ? `${title} | Fearless IT Las Vegas`
    : 'Fearless IT | Las Vegas Technology Solutions - Websites, Automation & Event Tech';
  
  const metaDescription = description || 
    'Technology that helps Las Vegas businesses grow, rank, and run flawlessly. Smart websites, AI automation, and event technology solutions.';
  
  const metaImage = image || defaultImage;
  const metaUrl = url ? `${siteUrl}${url}` : siteUrl;
  
  // Combine provided keywords with Las Vegas local keywords
  const baseKeywords = keywords || 'software development, custom software, cloud infrastructure, IT consulting, web development, mobile apps, SaaS, DevOps';
  const metaKeywords = `${baseKeywords}, ${LAS_VEGAS_KEYWORDS}`;

  // Local Business Schema for service pages
  const localServiceSchema = serviceName ? {
    "@context": "https://schema.org",
    "@type": "Service",
    "serviceType": serviceName,
    "provider": {
      "@type": "LocalBusiness",
      "name": "Fearless IT",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Las Vegas",
        "addressRegion": "NV",
        "addressCountry": "US"
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": 36.1699,
        "longitude": -115.1398
      }
    },
    "areaServed": {
      "@type": "City",
      "name": "Las Vegas"
    }
  } : null;

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="title" content={fullTitle} />
      <meta name="description" content={metaDescription} />
      <meta name="keywords" content={metaKeywords} />
      <link rel="canonical" href={metaUrl} />
      
      {/* Geo Tags for Las Vegas */}
      <meta name="geo.region" content="US-NV" />
      <meta name="geo.placename" content="Las Vegas" />
      <meta name="geo.position" content="36.1699;-115.1398" />
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={metaUrl} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={metaDescription} />
      <meta property="og:image" content={metaImage} />
      <meta property="og:image:secure_url" content={metaImage} />
      <meta property="og:image:type" content="image/png" />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={fullTitle} />
      <meta property="og:site_name" content="Fearless IT" />
      <meta property="og:locale" content="en_US" />
      
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={metaUrl} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={metaDescription} />
      <meta name="twitter:image" content={metaImage} />
      <meta name="twitter:image:alt" content={fullTitle} />
      
      {/* iMessage/Apple link preview */}
      <link rel="image_src" href={metaImage} />
      
      {/* Structured Data */}
      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      )}
      
      {/* Local Service Schema */}
      {localServiceSchema && (
        <script type="application/ld+json">
          {JSON.stringify(localServiceSchema)}
        </script>
      )}
    </Helmet>
  );
};

export default SEO;
